import React, { useMemo, useState } from "react";

// region External libraries
import { useQuery } from "@apollo/client";
import { format } from "date-fns";
// endregion External libraries
// region Hooks
import { useCommandMutation } from "@hooks/useCommandMutation";
import { useReachLabel } from "@hooks/useReachLabel";
// endregion Hooks
// region Languages
import useTranslation from "@languages/useTranslation";
import { AnnouncementMessages, GlobalMessages } from "@languages/interfaces";
// endregion Languages
// region Libraries
import { IconButton } from "@libraries/mui/components";
import { Add, Delete, Edit } from "@libraries/mui/icons";
// endregion Libraries
// region Atoms
import ActionDialog from "@atoms/ActionDialog";
import SearchField from "@atoms/SearchField";
import StateMessage from "@atoms/StateMessage";
// endregion Atoms
// region Molecules
import FormAnnouncement from "@molecules/FormAnnouncement";
import AnnouncementInteractionsDialog from "@molecules/AnnouncementInteractionsDialog";
import AnnouncementManagersDialog from "@molecules/AnnouncementManagersDialog";
// endregion Molecules
// region Store
import {
  AnnouncementManagementRow,
  announcementsForManagementQuery,
  announcementStatsQuery,
  TAnnouncementsForManagementQuery,
  TAnnouncementStatsQuery
} from "src/store/graphql/groups/announcement/query.graphql";
import {
  deleteAnnouncementMutation,
  TDeleteAnnouncementMutation
} from "src/store/graphql/groups/announcement/mutation.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";
// endregion Styles

// region Types
type TAnnouncementStatus = "active" | "scheduled" | "expired";

/** Form dialog target: null = closed, `{ row: null }` = create, `{ row }` = edit. */
interface IFormTarget {
  row: AnnouncementManagementRow | null;
}
// endregion Types

// region Constants
// Server-side cap of the management listing (P0 pagination rule).
const PAGE_SIZE = 50;

const pickDelete = (data: TDeleteAnnouncementMutation) => data.announcement.deleteAnnouncement;
// endregion Constants

// region Functions
const getAnnouncementStatus = (row: AnnouncementManagementRow): TAnnouncementStatus => {
  const now = Date.now();

  if (now < new Date(row.startDate).getTime()) return "scheduled";
  if (now > new Date(row.endDate).getTime()) return "expired";

  return "active";
};
// endregion Functions

/**
 * Announcements management dashboard: listing with period/status and
 * read/click counters, create/edit/delete, the per-announcement interaction
 * listing and the staff-permission ("manage access") dialog.
 */
const AnnouncementsDashboard: React.FC = () => {

  // region Hooks
  const { t } = useTranslation();
  const reachLabel = useReachLabel();
  // endregion Hooks

  // region Functions
  /** "Todos os clientes", or the audience in counts: "2 clientes · 1 pessoa". */
  const describeReach = (row: AnnouncementManagementRow) => (
    row.audience === "EVERYONE"
      ? t(AnnouncementMessages.reachEveryone)
      : reachLabel(row.targetTenants?.length ?? 0, row.targetUsers?.length ?? 0)
  );
  // endregion Functions

  // region States
  const [searchText, setSearchText] = useState("");
  const [formTarget, setFormTarget] = useState<IFormTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AnnouncementManagementRow | null>(null);
  const [interactionsTarget, setInteractionsTarget] = useState<AnnouncementManagementRow | null>(null);
  const [managersOpen, setManagersOpen] = useState(false);
  // endregion States

  // region Queries
  const { data: listData, loading: listLoading, refetch } = useQuery<TAnnouncementsForManagementQuery>(
    announcementsForManagementQuery,
    {
      variables: { page: 1, perPage: PAGE_SIZE },
      fetchPolicy: "network-only"
    }
  );

  const listPage = listData?.announcement?.getAnnouncementsForManagement;
  const rows = useMemo(() => listPage?.records ?? [], [listPage]);

  const announcementIds = useMemo(() => rows.map((row) => row.id), [rows]);

  const { data: statsData } = useQuery<TAnnouncementStatsQuery>(announcementStatsQuery, {
    variables: { announcementIds },
    skip: announcementIds.length === 0,
    fetchPolicy: "network-only"
  });
  // endregion Queries

  // region Mutations
  const remove = useCommandMutation<TDeleteAnnouncementMutation>(deleteAnnouncementMutation, pickDelete, { onSuccess: refetch });
  // endregion Mutations

  // region Constants
  const statsById = useMemo(() => {
    const map = new Map<string, { readCount: number; clickCount: number }>();

    (statsData?.announcement?.getAnnouncementStats ?? []).forEach((stat) => {
      map.set(stat.announcementId, { readCount: stat.readCount, clickCount: stat.clickCount });
    });

    return map;
  }, [statsData]);

  const visibleRows = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    if (!search) return rows;

    return rows.filter((row) => row.title.toLowerCase().includes(search));
  }, [rows, searchText]);

  const statusLabels: Record<TAnnouncementStatus, string> = {
    active: t(AnnouncementMessages.statusActive),
    scheduled: t(AnnouncementMessages.statusScheduled),
    expired: t(AnnouncementMessages.statusExpired)
  };

  const hasMoreRows = Boolean(listPage && listPage.totalCount > rows.length);
  // endregion Constants

  // region Handlers
  const handleFormSaved = () => {
    setFormTarget(null);
    refetch();
  };

  const confirmDelete = async () => {
    if (deleteTarget) await remove.run({ id: deleteTarget.id });

    setDeleteTarget(null);
  };
  // endregion Handlers

  return (
    <Styled.Container>
      <Styled.Header>
        <Styled.HeaderText>
          <Styled.PageTitle>{t(AnnouncementMessages.pageTitle)}</Styled.PageTitle>
          <Styled.PageSubtitle>{t(AnnouncementMessages.pageSubtitle)}</Styled.PageSubtitle>
        </Styled.HeaderText>
        <Styled.HeaderActions>
          <Styled.SearchBox>
            <SearchField
              fieldLabel={t(AnnouncementMessages.searchPlaceholder)}
              onSearchClick={(text) => setSearchText(text)}
              onClearClick={() => setSearchText("")}
            />
          </Styled.SearchBox>
          <Styled.GhostButton type="button" onClick={() => setManagersOpen(true)}>
            {t(AnnouncementMessages.manageAccess)}
          </Styled.GhostButton>
          <Styled.PrimaryButton type="button" onClick={() => setFormTarget({ row: null })}>
            <Add fontSize="small" />
            {t(AnnouncementMessages.newButton)}
          </Styled.PrimaryButton>
        </Styled.HeaderActions>
      </Styled.Header>

      <Styled.ListCard>
        <Styled.ListHeader>
          <span>{t(AnnouncementMessages.tableTitle)}</span>
          <span>{t(AnnouncementMessages.tablePeriod)}</span>
          <span>{t(AnnouncementMessages.tableStatus)}</span>
          <Styled.RightAligned>{t(AnnouncementMessages.tableReads)}</Styled.RightAligned>
          <Styled.RightAligned>{t(AnnouncementMessages.tableClicks)}</Styled.RightAligned>
          <Styled.RightAligned>{t(AnnouncementMessages.tableActions)}</Styled.RightAligned>
        </Styled.ListHeader>

        {listLoading && <StateMessage>{t(GlobalMessages.loading)}</StateMessage>}

        {!listLoading && visibleRows.length === 0 && (
          <StateMessage>{t(AnnouncementMessages.listEmpty)}</StateMessage>
        )}

        {!listLoading && visibleRows.map((row) => {
          const status = getAnnouncementStatus(row);
          const stats = statsById.get(row.id);

          return (
            <Styled.ListRow key={row.id}>
              <Styled.TitleCell>
                <Styled.RowTitle $expired={status === "expired"}>{row.title}</Styled.RowTitle>
                <Styled.RowPreview>{row.message}</Styled.RowPreview>
                <Styled.ReachLine title={describeReach(row)}>{describeReach(row)}</Styled.ReachLine>
              </Styled.TitleCell>
              <Styled.PeriodCell>
                <span>
                  {`${format(new Date(row.startDate), "dd/MM/yyyy HH:mm")} → ${format(new Date(row.endDate), "dd/MM/yyyy HH:mm")}`}
                </span>
                {row.createdByName && (
                  <Styled.AuthorLine>{t(AnnouncementMessages.byAuthor, { name: row.createdByName })}</Styled.AuthorLine>
                )}
              </Styled.PeriodCell>
              <div>
                <Styled.StatusChip $status={status}>{statusLabels[status]}</Styled.StatusChip>
              </div>
              <Styled.RightAligned>
                {stats && stats.readCount > 0 ? (
                  <Styled.InteractionsButton type="button" onClick={() => setInteractionsTarget(row)}>
                    {stats.readCount}
                  </Styled.InteractionsButton>
                ) : (
                  <span>{stats ? stats.readCount : "—"}</span>
                )}
              </Styled.RightAligned>
              <Styled.RightAligned>
                {stats && stats.clickCount > 0 ? (
                  <Styled.InteractionsButton type="button" onClick={() => setInteractionsTarget(row)}>
                    {stats.clickCount}
                  </Styled.InteractionsButton>
                ) : (
                  <span>{stats ? stats.clickCount : "—"}</span>
                )}
              </Styled.RightAligned>
              <Styled.ActionsCell>
                <IconButton
                  aria-label={t(AnnouncementMessages.formTitleEdit)}
                  size="small"
                  onClick={() => setFormTarget({ row })}
                >
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label={t(AnnouncementMessages.confirmDeleteTitle)}
                  size="small"
                  onClick={() => setDeleteTarget(row)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Styled.ActionsCell>
            </Styled.ListRow>
          );
        })}
      </Styled.ListCard>

      <Styled.Hint>
        {t(AnnouncementMessages.clicksHint)}
        {hasMoreRows && ` ${t(AnnouncementMessages.listTruncated, { shown: rows.length, total: listPage?.totalCount })}`}
      </Styled.Hint>

      {formTarget && (
        <FormAnnouncement
          currentAnnouncement={formTarget.row}
          onClose={() => setFormTarget(null)}
          onSaved={handleFormSaved}
        />
      )}

      <AnnouncementInteractionsDialog
        announcement={interactionsTarget}
        onClose={() => setInteractionsTarget(null)}
      />

      <AnnouncementManagersDialog open={managersOpen} onClose={() => setManagersOpen(false)} />

      <ActionDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title={t(AnnouncementMessages.confirmDeleteTitle)}
        actions={[
          { text: t(GlobalMessages.buttonCancel), action: () => { setDeleteTarget(null); } },
          { text: t(GlobalMessages.buttonConfirm), action: () => { confirmDelete(); } }
        ]}
      >
        {t(AnnouncementMessages.confirmDeleteMessage)}
      </ActionDialog>
    </Styled.Container>
  );
};

export default AnnouncementsDashboard;
